import { useRouter } from 'next/router';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ROUTE, SAMPLE_APPLICATION_ID } from 'src/apis/const';
import { User } from 'src/entity/user';
import { ABI } from 'src/hooks/use-create-badge-state/abi-state';
import {
  CreateBadgeState,
  CreateBadgeStateProvider,
  PageType,
} from 'src/hooks/use-create-badge-state/index';
import BadgeImageSection from './badge-image-section';
import BadgePreview from './badge-preview';
import EligibilityRuleSection from './eligibility-rules-section';
import GeneralInformation from './general-information';
import MemoSection from './memo-section';
import SubmitSection from './submit-section';

export enum TabType {
  GENERAL = 'GENERAL',
  IMAGE = 'IMAGE',
  ELIGIBILITY_RULE = 'ELIGIBILITY_RULE',
}

const tab: Record<
  TabType,
  { label: string; comp: any; info: string | React.ReactElement }
> = {
  [TabType.GENERAL]: {
    label: '1. General Information',
    comp: <GeneralInformation />,
    info: (
      <>
        <span
          style={{
            display: 'inline-block',
            fontWeight: 'bold',
            lineHeight: '2.4rem',
            fontSize: '1.6rem',
            marginBottom: 4,
          }}
        >
          Write text that describes your badge
        </span>
        <br />
        Please fill out the badge title, 1-liner eligibility description, and
        other metadata listed below. <br />
        Make sure that all the information accurately{' '}
        <span style={{ fontWeight: 'bold' }}>
          represents the project and the achievements.
        </span>
      </>
    ),
  },
  [TabType.IMAGE]: {
    label: '2. Badge Image',
    comp: <BadgeImageSection />,
    info: (
      <>
        <span
          style={{
            display: 'inline-block',
            fontWeight: 'bold',
            lineHeight: '2.4rem',
            fontSize: '1.6rem',
            marginBottom: 4,
          }}
        >
          Design a great badge NFT image
        </span>
        <br />
        You must select a badge thumbnail. Once you have uploaded, the default
        background image will be auto-generated. In addition, you can choose to
        upload a letter logo or a different background image.
      </>
    ),
  },
  [TabType.ELIGIBILITY_RULE]: {
    label: '3. Eligibility Rule',
    comp: <EligibilityRuleSection />,
    info: (
      <>
        <span
          style={{
            display: 'inline-block',
            fontWeight: 'bold',
            lineHeight: '2.4rem',
            fontSize: '1.6rem',
            marginBottom: 4,
          }}
        >
          Define actions and rules for your actions
        </span>
        <br />
        <b>Create action(s)</b> that you want to validate by entering a{' '}
        <b>contract address</b> and <b>selecting function(s)</b> and/or{' '}
        <b>eventlog(s)</b>. Please make sure to submit all and correct
        eligibility rules as your application can be rejected if you provide us
        with the wrong information. Noox team will reach out if we have any
        questions.
      </>
    ),
  },
};

const Tab: React.FC<{
  tabType: TabType;
  curTab: TabType;
  setCurTab: Dispatch<SetStateAction<TabType>>;
  minWidth: number;
}> = ({ tabType, curTab, setCurTab, minWidth }) => {
  return (
    <>
      <li
        className={`${
          curTab === tabType ? 'active b1-bold G25' : 'b1-regular G400'
        }`}
        key={tab[tabType].label}
        onClick={() => {
          setCurTab(tabType);
        }}
      >
        {tab[tabType].label}
      </li>
      <style jsx>
        {`
          li {
            min-width: ${minWidth}px;
          }
          li.active {
            color: var(--G25);
          }
          li.active::after {
            margin-top: 1px;
            content: '';
            width: calc(100% - 20px);
            height: 3px;
            display: block;
            background: var(--GR-PP);
          }
          li:not(.active):hover {
            opacity: 0.7;
            cursor: pointer;
          }
        `}
      </style>
    </>
  );
};

const CreateBadgeContainer: React.FC<{
  profile: User | undefined;
  defaultAbiValue?: Record<
    string,
    {
      abi: ABI | null;
      isCustomABI: boolean;
    }
  >;
  defaultValue?: CreateBadgeState;
  pageType: PageType;
}> = ({
  profile,
  defaultAbiValue = undefined,
  defaultValue = undefined,
  pageType,
}) => {
  const router = useRouter();
  const [curTab, setCurTab] = useState<TabType>(TabType.GENERAL);

  useEffect(() => {
    if (SAMPLE_APPLICATION_ID.includes(`${defaultValue?.id}`)) {
      return;
    }
    if (!profile) {
      router.push(ROUTE.USER_BADGE);
    }

    // if (
    //   profile &&
    //   defaultValue &&
    //   defaultValue.creatorAddress !== profile.address
    // ) {
    //   router.push(ROUTE.USER_BADGE);
    // }
  }, [profile, router, defaultValue]);

  return (
    <CreateBadgeStateProvider
      defaultValue={defaultValue}
      defaultAbiValue={defaultAbiValue}
      pageType={pageType}
    >
      <main className={`container`}>
        <div className="header">
          <h2 className="h4-bold G25">{pageType} Noox Badge</h2>
          <ul className="tab-section">
            <Tab
              tabType={TabType.GENERAL}
              curTab={curTab}
              setCurTab={setCurTab}
              minWidth={198}
            />
            <Tab
              tabType={TabType.IMAGE}
              curTab={curTab}
              setCurTab={setCurTab}
              minWidth={145}
            />
            <Tab
              tabType={TabType.ELIGIBILITY_RULE}
              curTab={curTab}
              setCurTab={setCurTab}
              minWidth={152}
            />
          </ul>
          {!(pageType === PageType.VIEW) && (
            <>
              <div className="info-tooltip SLG b2-regular">
                <span className="icon"></span>
                <span className="text">{tab[curTab].info}</span>
              </div>
              <MemoSection />
            </>
          )}
          <div className="divider"></div>
        </div>
        <div className="body">
          {(curTab === TabType.GENERAL || curTab === TabType.IMAGE) && (
            <div className="left">
              <div className="preview">
                <BadgePreview />
              </div>
            </div>
          )}
          <div className="right">
            <div className="comp">{tab[curTab].comp}</div>
            <div className="submit">
              <SubmitSection
                isEdit={pageType === PageType.EDIT}
                curTab={curTab}
                setCurTab={setCurTab}
              />
            </div>
          </div>
        </div>
      </main>
      <style jsx>
        {`
          main.container {
            width: 1200px;
            margin: 0 auto;
            height: auto;
          }
          .header {
          }
          .header h2 {
            margin-bottom: 20px;
          }
          .header .tab-section {
            display: flex;
            justify-content: flex-start;
            margin-bottom: 12px;
          }
          .info-tooltip {
            display: flex;
            padding: 8px 12px 8px 12px;
            background: linear-gradient(
              270deg,
              rgba(21, 122, 110, 0.2) 0%,
              rgba(30, 237, 167, 0.2) 100%
            );
            gap: 5.8px;
            border-radius: 12px;
            align-items: flex-start;
            margin-bottom: 20px;
          }
          .info-tooltip .icon {
            display: inline-block;
            width: 14.5px;
            height: 17.5px;
            margin-right: 5.8px;
            background: url(/image/white-info-icon.png) left 3px no-repeat;
            background-size: 14.5px 14.5px;
            margin-top: -1px;
          }
          .info-tooltip .text {
            width: calc(100% - 20.3px);
            color: var(--G25);
          }
          .header .divider {
            width: 100vw;
            height: 1px;

            margin-left: calc((100vw - 1200px) / 2 * -1);
            padding-top: 24px;
            margin-bottom: 40px;
          }
          .body {
            display: flex;
            gap: 40px;
          }
          .left {
            width: 387px;
          }
          .preview {
            width: 100%;
            height: 419px;
          }
          .right {
            width: ${curTab === TabType.ELIGIBILITY_RULE
              ? '100%'
              : 'calc(100% - 427px)'};
          }
          .right .comp {
            margin-bottom: 60px;
          }
        `}
      </style>
    </CreateBadgeStateProvider>
  );
};

export default CreateBadgeContainer;
